import { isNow } from '@intigral/utils';
import { useEffect, useRef, type RefObject } from 'react';
import calculateScrollLeft from './calculateScrollLeft';
import useScrollBehavior from '../useScrollBehavior';

type ScrollToProgramElementArgs = {
    programEl?: HTMLDivElement | null;
    scrollBehavior?: ScrollBehavior;
    scrollLeft?: number;
};

type UseChannelRowScrollArgs = {
    date?: any;
    inView: boolean;
    isRTL?: boolean;
    program: { data: { channelUuid?: string } };
    selectedProgram: any;
    programsWithStatus: any[];
    programContainer: RefObject<HTMLDivElement | null>;
    programRefs: RefObject<Map<string, HTMLDivElement>>;
    fetchPreviousProgramOnScroll?: (channelId: any) => void;
    fetchFutureProgramOnScroll?: (channelId: any) => void;
};

const SCROLL_EDGE_THRESHOLD = 400;
const FETCH_LOCK_TIMEOUT_MS = 1000;

const useChannelRowScroll = ({
    date,
    inView,
    isRTL,
    program,
    selectedProgram,
    programsWithStatus,
    programContainer,
    programRefs,
    fetchPreviousProgramOnScroll,
    fetchFutureProgramOnScroll,
}: UseChannelRowScrollArgs) => {
    const isFetchingPreviousPrograms = useRef(false);
    const isFetchingFuturePrograms = useRef(false);
    // Keeping track of the number of programs to detect when new programs are loaded (for infinite scroll)
    const previousProgramsCount = useRef(programsWithStatus.length);
    // fetch previous program when scroll to start of channel row - infinite scroll implementation
    const isFetchingRef = useRef(false);
    const ticking = useRef(false);
    const isEdgePosition = useRef(false);
    // scrollWidthBeforeFetch instead of preservedScrollWidth? to clarify that this is the scroll width before new programs are loaded, used to calculate the delta after loading
    const preservedScrollLeft = useRef<number | null>(null);
    // scrollWidthBeforeFetch instead of preservedScrollWidth? to clarify that this is the scroll width before new programs are loaded, used to calculate the delta after loading
    const preservedScrollWidth = useRef<number | null>(null);

    /**
     * Scrolls the channel row container to a target (selected program) position.
     *
     * Uses `scrollLeft` when provided, otherwise computes the target from `programEl`.
     */
    const scrollToProgramElement = ({
        programEl,
        scrollBehavior = 'instant',
        scrollLeft,
    }: ScrollToProgramElementArgs) => {
        if (!programContainer.current) {
            return;
        }

        let targetToScroll = 0;

        if (typeof scrollLeft === 'number') {
            targetToScroll = scrollLeft;
        } else if (programEl) {
            targetToScroll = calculateScrollLeft(programContainer.current, programEl, !!isRTL, 3);
        }

        const parentElement = programContainer.current;
        const currentScroll = parentElement.scrollLeft;

        if (Math.abs(currentScroll - targetToScroll) > 1) {
            parentElement.scrollTo({
                left: targetToScroll,
                behavior: scrollBehavior,
            });
        }
    };

    // Scroll to live program when date changes or when channel row comes into view, including on initial load
    useEffect(() => {
        if (!date && !inView) return;

        const liveProgram = programsWithStatus.find((p) => isNow(p, date));

        if (liveProgram && selectedProgram?.channelUuid !== liveProgram.channelUuid) {
            const programKey = `${liveProgram?.since}|${liveProgram?.till}`;
            const programRef = programRefs.current.get(programKey);

            if (!programRef) {
                return;
            }

            scrollToProgramElement({ programEl: programRef });
        }
    }, [date, inView, isRTL]);

    // Scroll to newly loaded programs when user scrolls to the end of the channel row and new programs are loaded
    // (infinite scroll)
    useEffect(() => {
        if (previousProgramsCount.current < programsWithStatus.length && programContainer.current) {
            const programsContainer = programContainer.current;
            const programElements = programContainer.current
                ? [...programContainer.current.querySelectorAll<HTMLDivElement>('[data-since]')]
                : [];

            // Number of newly loaded previous/future programs
            const loadedProgramsCount = programsWithStatus.length - previousProgramsCount.current;

            let selectedEl: HTMLDivElement | undefined;
            let nextEl: HTMLDivElement | undefined;

            const widthDelta = preservedScrollWidth.current
                ? programsContainer.scrollWidth - preservedScrollWidth.current
                : 0;

            if (isFetchingPreviousPrograms.current) {
                // Preserve scrolling position while previous programs are prepended by compensating with the added width.
                // Without this, the scroll position would jump to the left (to the first program) when programs are loaded.
                if (
                    typeof preservedScrollLeft.current === 'number' &&
                    typeof preservedScrollWidth.current === 'number'
                ) {
                    if (widthDelta > 0) {
                        // move to the top, outside of `if` to avoid code duplication?
                        const adjustedScrollLeft = isRTL
                            ? preservedScrollLeft.current
                            : preservedScrollLeft.current + widthDelta;
                        scrollToProgramElement({ scrollLeft: adjustedScrollLeft });
                    }
                    // else {
                    //     scrollToProgramElement({ scrollLeft: preservedScrollLeft.current });
                    // }
                }
                // is isFetchingFuturePrograms.current needed? instead of `else if` maybe just `else`?
            } else if (isFetchingFuturePrograms.current) {
                // Preserve scrolling position while future programs are appended.
                // This keeps loading in the background without snapping to the newly added programs.
                if (typeof preservedScrollLeft.current === 'number') {
                    const adjustedScrollLeft = isRTL
                        ? preservedScrollLeft.current + widthDelta
                        : // is this correct value for RTL?
                          preservedScrollLeft.current;
                    scrollToProgramElement({ scrollLeft: adjustedScrollLeft });
                }
            } else {
                // TODO: this is not needed?
                // last old program (anchor for instant scroll)
                nextEl = programElements[previousProgramsCount.current - 1];
                // New future programs were appended — the first new program is at index previousProgramsCount.
                selectedEl = programElements[previousProgramsCount.current];
            }

            if (nextEl) {
                scrollToProgramElement({ programEl: nextEl });
            }

            if (selectedEl) {
                scrollToProgramElement({ programEl: selectedEl, scrollBehavior: 'smooth' });
            }

            isFetchingPreviousPrograms.current = false;
            isFetchingFuturePrograms.current = false;
            preservedScrollLeft.current = null;
            preservedScrollWidth.current = null;
            previousProgramsCount.current = programsWithStatus.length;
        }
    }, [programsWithStatus.length]);

    const scrollBehavior = useScrollBehavior(inView);
    // TODO: logic for scrolling to selected/live/previous program is duplicated, can be extracted to a separate function
    useEffect(() => {
        // Only scroll if this is the selected channel and program is in view
        if (!programContainer.current || !selectedProgram || !inView) return;

        if (selectedProgram.channelUuid !== program.data.channelUuid) {
            return;
        }

        const programKey = `${selectedProgram?.since}|${selectedProgram?.till}`;
        const selectedRef = programRefs.current.get(programKey);

        if (!selectedRef) {
            return;
        }

        scrollToProgramElement({ programEl: selectedRef, scrollBehavior });
    }, [selectedProgram, inView, program.data.channelUuid, isRTL]);

    useEffect(() => {
        const programsContainer = programContainer.current;
        if (!programsContainer || !inView) return;

        const preserveCurrentScrollPosition = () => {
            preservedScrollLeft.current = programsContainer.scrollLeft;
            preservedScrollWidth.current = programsContainer.scrollWidth;
        };

        const handleScroll = () => {
            if (ticking.current) return;

            ticking.current = true;

            requestAnimationFrame(() => {
                const scrollLeft = programsContainer.scrollLeft;
                const scrollRightEdge = programsContainer.scrollLeft + programsContainer.clientWidth;
                const scrollWidth = programsContainer.scrollWidth;

                const isAtLeftStart = scrollLeft > 0 && scrollLeft <= SCROLL_EDGE_THRESHOLD;
                const isAtRightEnd = scrollRightEdge >= scrollWidth - SCROLL_EDGE_THRESHOLD;

                const releaseFetchLock = () => {
                    setTimeout(() => {
                        isFetchingRef.current = false;
                    }, FETCH_LOCK_TIMEOUT_MS);
                };

                if (isAtLeftStart && !isFetchingRef.current) {
                    isFetchingRef.current = true;

                    if (isEdgePosition.current) {
                        preserveCurrentScrollPosition();
                        fetchPreviousProgramOnScroll?.(program?.data?.channelUuid);
                        isFetchingPreviousPrograms.current = true;
                        isFetchingFuturePrograms.current = false;
                        isEdgePosition.current = false;
                    } else if (isRTL) {
                        preserveCurrentScrollPosition();
                        fetchFutureProgramOnScroll?.(program?.data?.channelUuid);
                        isFetchingFuturePrograms.current = true;
                        isFetchingPreviousPrograms.current = false;
                    } else {
                        preserveCurrentScrollPosition();
                        fetchPreviousProgramOnScroll?.(program?.data?.channelUuid);
                        isFetchingPreviousPrograms.current = true;
                        isFetchingFuturePrograms.current = false;
                    }

                    releaseFetchLock();
                }

                if (isAtRightEnd && !isFetchingRef.current) {
                    isFetchingRef.current = true;

                    if (isRTL) {
                        preserveCurrentScrollPosition();
                        fetchPreviousProgramOnScroll?.(program?.data?.channelUuid);
                        isFetchingPreviousPrograms.current = true;
                        isFetchingFuturePrograms.current = false;
                    } else {
                        preserveCurrentScrollPosition();
                        fetchFutureProgramOnScroll?.(program?.data?.channelUuid);
                        isFetchingFuturePrograms.current = true;
                        isFetchingPreviousPrograms.current = false;
                    }

                    releaseFetchLock();
                }

                ticking.current = false;
            });
        };

        programsContainer.addEventListener('scroll', handleScroll);

        return () => programsContainer.removeEventListener('scroll', handleScroll);
    }, [fetchPreviousProgramOnScroll, fetchFutureProgramOnScroll, program?.data?.channelUuid, inView, isRTL]);

    useEffect(() => {
        const d = new Date();
        const today = d.toISOString().split('T')[0];

        if (date < today) {
            isEdgePosition.current = true;
        }
    }, [date]);
};

export default useChannelRowScroll;
